"use client";

import { Eye } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { createRef, useEffect, useMemo, useRef, useState } from "react";
import { PageWrapper } from "@/components/core";
import { WalletAddress } from "@/components/core/WalletAddress/WalletAddress";
import { AnimatedBeam } from "@/components/ui/animated-beam";
import { Button } from "@/components/ui/button";
import ContractAddress from "@/contract/addresses";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

const contracts = [
  {
    name: "Stake",
    address: ContractAddress.STAKE,
    angle: 0,
  }, // Top
  {
    name: "Safe Vault",
    address: ContractAddress.DAO_SAFE_VAULT,
    angle: 45,
  }, // Top-right
  {
    name: "UVM",
    address: ContractAddress.UVM,
    angle: 90,
  }, // Right
  {
    name: "Election",
    address: ContractAddress.ELECTION,
    angle: 135,
  }, // Bottom-right
  {
    name: "Calculator",
    address: ContractAddress.CALCULATOR,
    angle: 180,
  }, // Bottom
  {
    name: "DNM",
    address: ContractAddress.DNM,
    angle: 225,
  }, // Bottom-left
  {
    name: "Dao",
    address: ContractAddress.DAO,
    angle: 270,
  }, // Left
  {
    name: "Wrapper",
    address: ContractAddress.WRAPPER,
    angle: 315,
  }, // Top-left
];

const gatewayContract = {
  name: "Gateway",
  address: ContractAddress.GATEWAY,
};

// Contract Node Component
const ContractNode = ({
  name,
  address,
  onClick,
  isActive = false,
}: {
  name: string;
  address: string;
  className?: string;
  onClick?: () => void;
  isActive?: boolean;
}) => {
  return (
    <div
      className={cn(
        "size-14 rounded-full border-2 shadow-[0_4px_20px_0_rgba(90,99,107,0.8)]",
        "bg-bg-primary border-stroke-subtle",
        "flex flex-col items-center justify-center",
        "z-20 text-text-primary",
        "shadow-[rgba(90,99,107,0.8)]",
        "select-none cursor-pointer",
        "transition-all duration-300 ease-in-out",
        "hover:scale-105",

        {
          "bg-accent text-accent-foreground shadow-accent/60 border-accent":
            isActive,
        },
      )}
      onClick={onClick}
    >
      <p className="text-[10px]">{name}</p>
      <div
        className={cn(
          "transition-all duration-300 ease-in-out overflow-hidden",
          isActive ? "max-h-20 opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <WalletAddress
          className="flex-col text-[8px] space-x-0 space-y-0"
          size={12}
          address={address}
          truncateSize="xs"
        />
      </div>
    </div>
  );
};

const SmartContractsPage = () => {
  const t = useTranslations("smartContracts");
  const [activeContract, setActiveContract] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // Refs for AnimatedBeam
  const containerRef = useRef<HTMLDivElement>(null);
  const gatewayRef = useRef<HTMLDivElement>(null);

  const contractRefs = useMemo(() => {
    const refs: Record<string, React.RefObject<HTMLDivElement | null>> = {};
    contracts.forEach((c) => {
      refs[c.name] = createRef<HTMLDivElement>();
    });
    return refs;
  }, []);

  const handleContractClick = (contract: { name: string; address: string }) => {
    if (activeContract === contract.name) {
      setActiveContract(null);
      return;
    }
    setActiveContract(contract.name);
  };

  const handleGatewayClick = () => {
    setActiveContract("Gateway");
  };

  useEffect(() => {
    const allReady =
      gatewayRef.current && Object.values(contractRefs).every((r) => r.current);

    if (allReady) setReady(true);
  }, [contractRefs]);

  return (
    <PageWrapper
      className="flex flex-col items-center h-screen justify-between gap-y-4"
      containerClassName="px-4"
    >
      <div className="flex flex-col items-center justify-between">
        <div className="space-y-4">
          <h1 className="text-lg font-bold text-text-primary">{t("title")}</h1>
          <p className="text-text-secondary text-sm">{t("description")}</p>
        </div>

        <div
          ref={containerRef}
          className="relative mt-4 w-full h-[50vh] mx-auto flex items-center justify-center"
        >
          {/* Gateway Contract - True Center */}
          <div ref={gatewayRef} className="absolute z-20">
            <ContractNode
              name={gatewayContract.name}
              address={gatewayContract.address}
              onClick={handleGatewayClick}
              isActive={activeContract === "Gateway"}
              className="w-24 h-24"
            />
          </div>

          {/* Surrounding Contracts - Proper Circle */}
          {contracts.map((contract) => {
            const radius = 140;
            const angle = contract.angle - 90;

            const x = Math.cos((angle * Math.PI) / 180) * radius;
            const y = Math.sin((angle * Math.PI) / 180) * radius;

            return (
              <div
                key={`circle-${contract.address}`}
                ref={contractRefs[contract.name]}
                className="absolute z-50"
                style={{
                  left: `calc(50% + ${x}px)`,
                  top: `calc(50% + ${y}px)`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <ContractNode
                  name={contract.name}
                  address={contract.address}
                  onClick={() => handleContractClick(contract)}
                  isActive={activeContract === contract.name}
                />
              </div>
            );
          })}

          {/* Animated Beams from Gateway to each contract */}
          {ready && contracts.every((c) => contractRefs[c.name]?.current) && (
            <>
              {contracts.map((contract) => (
                <AnimatedBeam
                  key={`beam-${contract.address}`}
                  containerRef={containerRef}
                  fromRef={gatewayRef}
                  toRef={contractRefs[contract.name]}
                  reverse={contract.angle > 180}
                  pathColor="var(--stroke-light)"
                  pathWidth={2}
                  gradientStartColor="var(--stroke-light)"
                  gradientStopColor="var(--stroke-light)"
                  delay={0}
                  duration={10}
                />
              ))}
            </>
          )}
        </div>

        {/* Contract Details */}
        {activeContract && (
          <Button
            colors="primary"
            variant="solid"
            startIcon={<Eye size={16} />}
            asChild
          >
            <Link
              href={`${ContractAddress.POLYGON_ADDRESS_SCAN}${
                activeContract === "Gateway"
                  ? gatewayContract.address
                  : contracts.find((c) => c.name === activeContract)?.address
              }`}
            >
              <span className="flex gap-x-2 items-center">
                <Eye /> {t("viewDetails")}
              </span>
            </Link>
          </Button>
        )}
      </div>
    </PageWrapper>
  );
};

export default SmartContractsPage;
